import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  SvgIcon,
  SvgIconButton,
  Tabs,
  Tooltip,
  Typography
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { actions as authActions, selectors as authSelectors } from 'store/auth';
import { selectors as clientsSelectors } from 'store/clients';
import { actions as venueActions } from 'store/venues';
import { getMatchPath } from 'utils/urlUtils';
import packageJSON from '../../../package.json';
import { useStyles } from './styled';

// RETURNS THE PATHNAME WITHOUT THE KIOSKID
const pathInit = (path: string) => {
  const match = getMatchPath(path, '/kiosks/:kioskId?');

  // IF THERE IS A MATCH OF PATH AND THAT PATH HAS KIOSKID, SLICE IT OUT
  if (match && match.params.kioskId) return path.slice(0, path.lastIndexOf('/')).trim();
  return path;
};

export interface MenuPanelProps {
  isOpen: boolean;
  onClose: () => void;
  routePath: string;
}

export const MenuPanel = (props: MenuPanelProps) => {
  const { isOpen, onClose, routePath } = props;

  const { logout } = useActions({ logout: authActions.logout });
  const { resetVenueAction } = useActions({ resetVenueAction: venueActions.resetAction });

  const admin = useSelector(authSelectors.selectAdmin);
  const activeClient = useSelector(clientsSelectors.selectClient);

  const path = useMemo(() => pathInit(routePath), [routePath]);
  const [to, setTo] = useState<string>(path);
  const [readOnlyAdmin, setReadOnlyAdmin] = useState(false);
  const [globalAdmin, setGlobalAdmin] = useState(false);

  const classes = useStyles();

  const activeClientName =
    activeClient?.name && activeClient.name.length > 20
      ? `${activeClient.name.slice(0, 20)}...`
      : (activeClient?.name ?? '');

  useEffect(() => {
    if (!path.includes(to)) {
      setTo(path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  // check for user is Read-Only Admin or not, if yes hide Media center tab
  useEffect(() => {
    if (admin && admin.role === 'GlobalAdmin') {
      setGlobalAdmin(true);
    } else if (admin && admin.role === 'ReadOnlyAdmin') {
      setReadOnlyAdmin(true);
    }
  }, [admin]);

  const storeClearingAndLogout = () => {
    onClose();
    resetVenueAction();
    logout();
  };

  const openSupportLink = () => {
    window.open(`https://kistechnologies.atlassian.net/servicedesk/customer/portal/4`);
  };

  const onChangeRoute = () => {
    onClose();
  };

  return (
    <Drawer open={isOpen} onClose={onClose} hasBorderRadius anchor="left">
      <Grid container direction="column" className={classes.menuPanelContainer}>
        <Grid direction="row" className={classes.menuPanelHeader}>
          <Typography fontSize={24} fontWeight={600}>
            Menu
          </Typography>
          <SvgIconButton appearance="transparent" onClick={onClose} data-testid="ticket-log-x">
            <SvgIcon name="exit_circle" size={32} />
          </SvgIconButton>
        </Grid>
        <Divider />
        <>
          {activeClient && globalAdmin && (
            <Box margin="4 0 2">
              <Typography className={classes.activeCustomerText}>
                Active customer:
                <br />
                <Tooltip title={activeClient.name} arrow placement="top" verticalOffset={12}>
                  <span>{activeClientName}</span>
                </Tooltip>
              </Typography>
            </Box>
          )}
        </>
        <Grid container direction="column" className={classes.menuPanelTabContainer}>
          <Grid className={classes.menuPanelItemsBlock}>
            <Tabs value={to} onChange={onChangeRoute}>
              {globalAdmin && (
                <Tabs.Tab iconName="customer" iconViewBox="0 0 24 24" label="Customers" to="/customers" />
              )}
              <Tabs.Tab iconName="kiosks" label="Kiosks" to="/kiosks" />
              <Tabs.Tab iconName="credential" label="Users" to="/users" />
              <Tabs.Tab iconName="activity_log" iconViewBox="0 0 59 36" label="Activity Log" to="/activityLog" />
              <Tabs.Tab iconName="ticket" iconViewBox="-5 0 59 36" label="Delivery Log" to="/deliveryLog" />
              {!readOnlyAdmin && <Tabs.Tab iconName="media_center" label="Media Center" to="/mediaCenter" />}
              {!readOnlyAdmin && (
                <Tabs.Tab iconViewBox="0 0 24 24" iconName="settings" label="Settings" to="/settings" />
              )}
              {globalAdmin && (
                <Tabs.Tab iconViewBox="0 0 24 24" iconName="settings" label="Configuration" to="/configuration" />
              )}
            </Tabs>
          </Grid>
          <Grid className={classes.menuPanelFooter}>
            <Button customSize="small" onClick={openSupportLink} className={classes.menuPanelButton}>
              Support
            </Button>
            <Button customSize="small" onClick={storeClearingAndLogout} className={classes.menuPanelButton}>
              Log Out
            </Button>
            <Box margin="2 0 4 0">
              <Typography fontSize={12} align="center">
                Version: {packageJSON.version}
              </Typography>
              <Typography fontSize={12} align="center">
                ©KIS Technologies
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Drawer>
  );
};
